import classNames from 'classnames'
import type { FunctionComponent, JSX, PropsWithChildren } from 'react'
import ReactPlayer from 'react-player'
import type { ReactPlayerProps } from 'react-player/dist/types'

import styles from './video.module.scss'
import type { VideoType } from '../../types/video'
import { Play } from '../icons/play'
import { useDidMount } from '@lib/hooks/useDidMount'
import type { WithTestId } from '@lib/utils/testid'

/**
 * Uses react-player to display a video.
 * Lazy: only shows the poster unless the user clicks on it.
 */
export const Video: FunctionComponent<PropsWithChildren<Props>> = ({
    sources,
    poster,
    alt = '',
    width,
    height,
    preset,
    className,
    'data-testid': testId,
    playing = true,
    loop = false,
    ...restOfProps
}) => {
    const mounted = useDidMount()

    const posterImage = (
        <img
            role='poster'
            className={styles.poster}
            src={poster}
            width={width}
            height={height}
            alt='video'
        />
    )

    // don't render the video player on the server side. see: https://github.com/cookpete/react-player/issues/1474
    if (!mounted) {
        return posterImage
    }

    if (preset === 'gif') {
        if (!mounted)
            return (
                <div className={classNames(styles.video, className)}>
                    {posterImage}
                </div>
            )

        return (
            <video
                muted
                loop
                playsInline
                webkit-playsinline='true'
                autoPlay
                controls={false}
                width={width}
                height={height}
                className={classNames(styles.video, className)}
                poster={poster}
                title={alt}
            >
                {sources.map(
                    ({
                        source,
                        contentType,
                    }: {
                        source: string
                        contentType: string
                    }) => (
                        <source key={source} src={source} type={contentType} />
                    ),
                )}
            </video>
        )
    }

    return (
        <figure
            className={classNames(styles.wrapper, className)}
            role='video'
            data-testid={testId}
        >
            <ReactPlayer
                playing={playing}
                controls
                light={poster}
                playIcon={
                    <Play
                        width='75'
                        height='75'
                        data-testid='video.play'
                        className={styles.play}
                        color='step-right-before-midnight'
                        secondaryColor='step-white'
                    />
                }
                wrapper={({ children }) => (
                    <div
                        className={styles.player}
                        title={alt}
                        data-testid='video.wrapper'
                    >
                        {children}
                    </div>
                )}
                src={sources.map(({ source }) => source)[0]}
                loop={loop}
                {...restOfProps}
            />
            {/** This is a hack to set the size of the container. It's not visible. */}
            <div className={styles.poster} style={{ width, height }}>
                {posterImage}
            </div>
        </figure>
    )
}

type Props = WithTestId<
    Partial<ReactPlayerProps> & VideoType & { className?: string }
> &
    JSX.IntrinsicAttributes
